import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { Star, ShieldCheck, Camera } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

/** Public reviews block, mounted on `/shop/:handle`.
 *  Silent if the product has no reviews yet — never shows fake data. */
export function ProductReviews({ handle }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    shopApi.get(`/shop/products/${handle}/reviews?limit=50`)
      .then((r) => setData(r.data))
      .catch(() => setData({ count: 0, reviews: [] }));
  }, [handle]);

  if (!data || data.count === 0) return null;

  const stars = (n, size = 12) => (
    <span className="inline-flex" aria-label={`${n} of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i} size={size}
          className={i <= Math.round(n) ? "fill-[color:var(--hq-cyan)] text-[color:var(--hq-cyan)]" : "text-white/20"}
        />
      ))}
    </span>
  );

  return (
    <section className="mt-16 border-t border-white/10 pt-10" data-testid="product-reviews">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h2 className="font-display text-2xl">{t(lang, "reviews_title")}</h2>
        <div className="flex items-center gap-2">
          {stars(data.average_rating, 14)}
          <span className="font-mono text-sm">{data.average_rating}</span>
          <span className="font-mono text-[10px] uppercase text-white/40">
            · {data.count} {t(lang, "reviews_count")}
          </span>
        </div>
        {data.verified_count > 0 && (
          <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-[color:var(--hq-success)]">
            <ShieldCheck size={10} /> {data.verified_count} {t(lang, "reviews_verified")}
          </span>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {data.reviews.map((r) => {
          const isLong = r.body && r.body.length > 320;
          const shown = expanded[r.review_id] || !isLong ? r.body : r.body.slice(0, 320) + "…";
          return (
            <article key={r.review_id} className="border border-white/10 p-4" data-testid={`review-${r.review_id}`}>
              <header className="flex items-center gap-2 mb-2 flex-wrap">
                {stars(r.rating)}
                <span className="text-sm">{r.author}</span>
                {r.country && (
                  <span className="font-mono text-[10px] uppercase text-white/40">{r.country}</span>
                )}
                {r.verified_purchase && (
                  <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase text-[color:var(--hq-success)] ml-auto">
                    <ShieldCheck size={10} /> {t(lang, "reviews_verified_purchase")}
                  </span>
                )}
              </header>
              <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{shown}</p>
              {isLong && (
                <button
                  onClick={() => setExpanded((e) => ({ ...e, [r.review_id]: !e[r.review_id] }))}
                  className="mt-1 text-[10px] font-mono uppercase text-[color:var(--hq-cyan)]"
                  data-testid={`review-expand-${r.review_id}`}
                >
                  {expanded[r.review_id] ? t(lang, "reviews_less") : t(lang, "reviews_more")}
                </button>
              )}
              {r.images && r.images.length > 0 && (
                <div className="mt-3 flex gap-2 flex-wrap">
                  {r.images.slice(0, 6).map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer" className="w-14 h-14 border border-white/10 overflow-hidden">
                      <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              )}
              <footer className="mt-3 flex items-center justify-between font-mono text-[10px] uppercase text-white/40">
                <span>{r.source}</span>
                {r.purchased_at && <span>{r.purchased_at.slice(0, 10)}</span>}
              </footer>
            </article>
          );
        })}
      </div>
    </section>
  );
}
